import { memo, useRef, useEffect, useCallback } from 'react';
import type { ConnectionStatus } from '@/hooks/useDeepgramTranscriber';

interface ImmersiveWaveformProps {
  analyserRef: React.MutableRefObject<AnalyserNode | null>;
  connectionStatus: ConnectionStatus;
}

const BAR_WIDTH_RATIO = 0.5;
const SMOOTHING = 0.3; // 0 = no smoothing, 1 = frozen

/** CSS変数 --primary からテーマカラーを取得 */
function getThemeHsl(): string {
  return getComputedStyle(document.documentElement).getPropertyValue('--primary').trim();
}
function themePrimary(): string {
  const hsl = getThemeHsl();
  return hsl ? `hsl(${hsl})` : '#4F46E5';
}
function themeSecondary(): string {
  const hsl = getThemeHsl();
  return hsl ? `hsl(${hsl} / 0.6)` : '#818CF8';
}
function themeWithAlpha(alpha: number): string {
  const hsl = getThemeHsl();
  return hsl ? `hsl(${hsl} / ${alpha})` : `rgba(79, 70, 229, ${alpha})`;
}

const ImmersiveWaveform = memo<ImmersiveWaveformProps>(
  ({ analyserRef, connectionStatus }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animationFrameRef = useRef<number>(0);
    const frozenDataRef = useRef<Uint8Array | null>(null);
    const idlePhaseRef = useRef(0);
    const smoothedRef = useRef<Float32Array | null>(null);

    const isRecording = connectionStatus === 'recording';
    const isPaused = connectionStatus === 'paused';
    const isActive = isRecording || isPaused;

    const setupCanvas = useCallback(() => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.scale(dpr, dpr);
      return { ctx, width: rect.width, height: rect.height };
    }, []);

    const drawBars = useCallback(
      (ctx: CanvasRenderingContext2D, width: number, height: number, data: Uint8Array | null) => {
        ctx.clearRect(0, 0, width, height);

        // レスポンシブ: 画面幅に応じてバー数を調整
        const barCount = Math.max(24, Math.min(80, Math.floor(width / 6)));
        const slotWidth = width / barCount;
        const barWidth = slotWidth * BAR_WIDTH_RATIO;
        const centerY = height / 2;
        const half = barCount / 2;

        // smoothed バッファ初期化・リサイズ
        if (!smoothedRef.current || smoothedRef.current.length !== barCount) {
          smoothedRef.current = new Float32Array(barCount);
        }
        const smoothed = smoothedRef.current;

        const pc = themePrimary();
        const sc = themeSecondary();

        for (let i = 0; i < barCount; i++) {
          // 中央基準: 中央バーが低周波(音声のメインエネルギー)、端が高周波
          const distFromCenter = Math.abs(i - half + 0.5) / half;
          const dataIndex = data ? Math.floor(distFromCenter * (data.length * 0.6)) : 0;
          const raw = data ? (data[dataIndex] || 0) / 255 : 0;

          // 感度ブースト + スムージング
          const boosted = Math.min(1, raw * 2.0 + 0.08);
          smoothed[i] = smoothed[i] * SMOOTHING + boosted * (1 - SMOOTHING);

          const barHeight = Math.max(2, height * smoothed[i] * 0.45);
          const x = i * slotWidth + (slotWidth - barWidth) / 2;

          const gradient = ctx.createLinearGradient(x, centerY - barHeight, x, centerY + barHeight);
          gradient.addColorStop(0, sc);
          gradient.addColorStop(0.5, pc);
          gradient.addColorStop(1, sc);

          ctx.fillStyle = gradient;
          ctx.shadowColor = pc;
          ctx.shadowBlur = 6;

          // Mirror bars: 中央から上下に描画
          ctx.beginPath();
          ctx.roundRect(x, centerY - barHeight, barWidth, barHeight, barWidth / 2);
          ctx.fill();
          ctx.beginPath();
          ctx.roundRect(x, centerY, barWidth, barHeight, barWidth / 2);
          ctx.fill();
        }

        ctx.shadowBlur = 0;
      },
      []
    );

    const drawIdleSine = useCallback(
      (ctx: CanvasRenderingContext2D, width: number, height: number) => {
        ctx.clearRect(0, 0, width, height);
        idlePhaseRef.current += 0.02;

        const barCount = Math.max(24, Math.min(80, Math.floor(width / 6)));
        const slotWidth = width / barCount;
        const barWidth = slotWidth * BAR_WIDTH_RATIO;
        const centerY = height / 2;
        const half = barCount / 2;

        for (let i = 0; i < barCount; i++) {
          // 中央基準: 中央から端に向かって波打つ
          const distFromCenter = Math.abs(i - half + 0.5) / half;
          const sine = Math.sin(idlePhaseRef.current + distFromCenter * 4) * 0.5 + 0.5;
          const barHeight = 2 + sine * height * 0.06;
          const x = i * slotWidth + (slotWidth - barWidth) / 2;

          ctx.fillStyle = themeWithAlpha(0.2 + sine * 0.15);
          ctx.beginPath();
          ctx.roundRect(x, centerY - barHeight, barWidth, barHeight, barWidth / 2);
          ctx.fill();
          ctx.beginPath();
          ctx.roundRect(x, centerY, barWidth, barHeight, barWidth / 2);
          ctx.fill();
        }
      },
      []
    );

    useEffect(() => {
      const result = setupCanvas();
      if (!result) return;
      const { ctx, width, height } = result;
      if (!ctx) return;

      const analyser = analyserRef.current;
      const bufferLength = analyser ? analyser.frequencyBinCount : 0;
      const dataArray = bufferLength > 0 ? new Uint8Array(bufferLength) : null;

      const animate = () => {
        animationFrameRef.current = requestAnimationFrame(animate);

        if (isRecording && analyser && dataArray) {
          analyser.getByteFrequencyData(dataArray);
          frozenDataRef.current = new Uint8Array(dataArray);
          drawBars(ctx, width, height, dataArray);
        } else if (isPaused && frozenDataRef.current) {
          drawBars(ctx, width, height, frozenDataRef.current);
          ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
          ctx.fillRect(0, 0, width, height);
        } else {
          drawIdleSine(ctx, width, height);
        }
      };

      animate();

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }, [analyserRef, isRecording, isPaused, isActive, setupCanvas, drawBars, drawIdleSine]);

    // Handle resize
    useEffect(() => {
      const handleResize = () => setupCanvas();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [setupCanvas]);

    return (
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{ display: 'block' }}
      />
    );
  }
);
ImmersiveWaveform.displayName = 'ImmersiveWaveform';

export default ImmersiveWaveform;
